/* =========================================================================
   Light My House — Landing page behaviour
   No dependencies. Everything degrades to a working page without JS.
   ========================================================================= */
(function () {
  'use strict';

  var DRAWER_SECTION = 'lmh-cart-drawer';
  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */
  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }
  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function on(el, evt, fn, opts) {
    if (el) el.addEventListener(evt, fn, opts);
  }
  /* The theme editor re-runs init on every section reload. Document-level
     listeners must not stack up, or one click would add two items. */
  var done = {};
  function once(key, fn) {
    if (done[key]) return;
    done[key] = true;
    fn();
  }
  function markOnce(el) {
    if (!el || el.dataset.lmhBound === '1') return false;
    el.dataset.lmhBound = '1';
    return true;
  }

  /* ------------------------------------------------------------ cart drawer */
  var Drawer = {
    el: null,          // our own drawer, when we are using it
    themeEl: null,     // the theme's <cart-drawer>, when we are handing off
    mode: 'lmh',
    lastFocus: null,

    /* Which sections to ask Shopify to re-render on every cart change. */
    sections: function () {
      /* cart-icon-bubble is inline markup inside the theme header on this
         shop, not a section of its own, so there is nothing to re-render. Our
         header carries the count and the theme header is hidden here anyway. */
      return Drawer.mode === 'theme'
        ? 'cart-drawer,' + DRAWER_SECTION
        : DRAWER_SECTION;
    },

    /* True when there is somewhere to put the cart, either way. */
    active: function () {
      return !!(Drawer.el || Drawer.themeEl);
    },

    init: function () {
      var host = $('[data-lmh-drawer-mode]');
      var wanted = host ? host.dataset.lmhDrawerMode : 'lmh';

      if (wanted === 'theme') {
        var theme = document.querySelector('cart-drawer');
        /* Only hand off if the theme's drawer is really there and behaves the
           way we expect. If the shop's cart type is set to page or notification
           it will not exist, so we quietly keep our own. */
        if (theme && typeof theme.renderContents === 'function') {
          Drawer.mode = 'theme';
          Drawer.themeEl = theme;
          Drawer.pairingHost = host;
          return;
        }
      }

      Drawer.el = $('[data-lmh-drawer]');
      if (!Drawer.el || !markOnce(Drawer.el)) return;

      on(Drawer.el, 'click', function (e) {
        if (e.target.closest('[data-lmh-drawer-close]')) {
          e.preventDefault();
          Drawer.close();
        }
        var step = e.target.closest('[data-lmh-qty-btn]');
        if (step) {
          e.preventDefault();
          Drawer.change(step.dataset.lmhLine, parseInt(step.dataset.lmhQty, 10), step);
        }
      });

      on(document, 'keydown', function (e) {
        if (e.key === 'Escape' && Drawer.el.classList.contains('is-open')) Drawer.close();
      });
    },

    open: function () {
      if (Drawer.mode === 'theme') {
        if (Drawer.themeEl && typeof Drawer.themeEl.open === 'function') Drawer.themeEl.open();
        return;
      }
      if (!Drawer.el) return;
      Drawer.lastFocus = document.activeElement;
      Drawer.el.classList.add('is-open');
      Drawer.el.setAttribute('aria-hidden', 'false');
      root.style.overflow = 'hidden';
      var close = $('[data-lmh-drawer-close]', Drawer.el);
      if (close) window.setTimeout(function () { close.focus(); }, 60);
    },

    close: function () {
      if (Drawer.mode === 'theme') {
        if (Drawer.themeEl && typeof Drawer.themeEl.close === 'function') Drawer.themeEl.close();
        return;
      }
      if (!Drawer.el) return;
      Drawer.el.classList.remove('is-open');
      Drawer.el.setAttribute('aria-hidden', 'true');
      root.style.overflow = '';
      if (Drawer.lastFocus) Drawer.lastFocus.focus();
    },

    /* In theme mode Dawn renders its own drawer from the response. All we do
       afterwards is lift our layer suggestions out of our section's HTML and
       place them above Dawn's footer. */
    renderTheme: function (data) {
      try {
        Drawer.themeEl.classList.remove('is-empty');
        Drawer.themeEl.renderContents(data);
      } catch (e) {
        return Drawer.hardReload();
      }

      var ours = data.sections && data.sections[DRAWER_SECTION];
      if (!ours) return;

      window.setTimeout(function () {
        var parsed = new DOMParser().parseFromString(ours, 'text/html');
        var pairing = parsed.querySelector('.lmh-pairing');
        var inner = Drawer.themeEl.querySelector('.drawer__inner') || Drawer.themeEl;
        var existing = inner.querySelector('.lmh-pairing');
        if (existing) existing.remove();
        if (!pairing) return;

        pairing.classList.add('lmh', 'lmh-pairing--theme');
        var footer = inner.querySelector('.drawer__footer') || inner.querySelector('.cart-drawer__footer');
        if (footer && footer.parentNode) {
          footer.parentNode.insertBefore(pairing, footer);
        } else {
          inner.appendChild(pairing);
        }
      }, 0);
    },

    hardReload: function () {
      window.location.href = '/cart';
    },

    /* Swap in freshly rendered markup from the Section Rendering API */
    render: function (html) {
      if (!Drawer.el || !html) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fresh = $('[data-lmh-drawer-contents]', parsed);
      var current = $('[data-lmh-drawer-contents]', Drawer.el);
      if (fresh && current) current.innerHTML = fresh.innerHTML;

      var freshCount = $('[data-lmh-cart-count]', parsed);
      if (freshCount) {
        $$('[data-lmh-cart-count]').forEach(function (node) {
          node.textContent = freshCount.textContent;
          node.hidden = freshCount.hidden;
        });
      }
    },

    refresh: function () {
      return fetch(window.location.pathname + '?sections=' + Drawer.sections())
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (Drawer.mode === 'theme') return Drawer.renderTheme({ sections: data });
          Drawer.render(data[DRAWER_SECTION]);
        });
    },

    change: function (line, quantity, button) {
      var body = { line: parseInt(line, 10), quantity: quantity, sections: Drawer.sections(), sections_url: window.location.pathname };
      Drawer.el.setAttribute('aria-busy', 'true');
      if (button) button.setAttribute('aria-busy', 'true');
      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/change.js' : '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.sections && data.sections[DRAWER_SECTION]) {
            Drawer.render(data.sections[DRAWER_SECTION]);
          } else {
            return Drawer.refresh();
          }
        })
        .catch(function () { window.location.href = '/cart'; })
        .finally(function () { Drawer.el.removeAttribute('aria-busy'); });
    }
  };

  /* --------------------------------------------------------- add to cart */
  /* Delegated on purpose: the drawer re-renders its own markup after every
     change, so anything bound directly to a form inside it would go dead. */
  function addToCart(form) {
    var btn = form.querySelector('button[type="submit"]');
    var idField = form.querySelector('input[name="id"]');
    if (!idField) return;
    var id = idField.value;
    if (!id || (btn && btn.getAttribute('aria-busy') === 'true')) return;

    if (btn) btn.setAttribute('aria-busy', 'true');

    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(id), quantity: 1 }],
        sections: Drawer.sections(),
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status && data.status !== 200) throw new Error(data.description || 'Add to cart failed');

        if (Drawer.mode === 'theme') {
          Drawer.renderTheme(data);
          return;
        }
        if (data.sections && data.sections[DRAWER_SECTION]) {
          Drawer.render(data.sections[DRAWER_SECTION]);
        } else {
          return Drawer.refresh();
        }
      })
      .then(function () {
        if (Quick.el && !Quick.el.hidden) Quick.close();
        Drawer.open();
        if (btn && document.contains(btn)) {
          btn.classList.add('is-done');
          window.setTimeout(function () { btn.classList.remove('is-done'); }, 2200);
        }
        if (form.dataset.lmhPixel === 'true' && typeof window.fbq === 'function') {
          window.fbq('track', 'AddToCart', {
            content_ids: [form.dataset.lmhSku || id],
            content_type: 'product',
            value: parseFloat(form.dataset.lmhValue || '0'),
            currency: form.dataset.lmhCurrency || 'GBP'
          });
        }
      })
      .catch(function () { window.location.href = '/cart'; })
      .finally(function () {
        if (btn && document.contains(btn)) btn.removeAttribute('aria-busy');
      });
  }

  function bindAddToCart() {
    once('add', function () {
    on(document, 'submit', function (e) {
      var form = e.target.closest('[data-lmh-add]');
      if (!form) return;
      if (!Drawer.active()) return; // no drawer at all, let the form post normally
      e.preventDefault();
      addToCart(form);
    });

    on(document, 'click', function (e) {
      var opener = e.target.closest('[data-lmh-cart-open]');
      if (!opener || !Drawer.active()) return;
      e.preventDefault();
      Drawer.open();
    });
    });
  }

  /* ---------------------------------------------------------- quick view */
  var Quick = {
    el: null,
    product: null,
    options: {},

    ensure: function () {
      if (Quick.el) return Quick.el;
      var el = document.createElement('div');
      el.className = 'lmh lmh-quick';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', 'Choose options');
      el.hidden = true;
      el.innerHTML =
        '<button class="lmh-quick__scrim" type="button" data-lmh-quick-close aria-label="Close"></button>' +
        '<div class="lmh-quick__panel"><div data-lmh-quick-body></div></div>';
      document.body.appendChild(el);
      Quick.el = el;

      on(el, 'click', function (e) {
        if (e.target.closest('[data-lmh-quick-close]')) { e.preventDefault(); Quick.close(); return; }
        var opt = e.target.closest('[data-lmh-opt]');
        if (opt) { Quick.choose(opt.dataset.lmhOptName, opt.dataset.lmhOptValue); }
      });
      on(document, 'keydown', function (e) {
        if (e.key === 'Escape' && !el.hidden) Quick.close();
      });
      return el;
    },

    open: function (url, name) {
      var el = Quick.ensure();
      el.hidden = false;
      Quick.openedOverDrawer = !!(Drawer.el && Drawer.el.classList.contains('is-open'));
      root.style.overflow = 'hidden';
      $('[data-lmh-quick-body]', el).innerHTML = '<p class="lmh-quick__loading">Loading options…</p>';

      fetch(url + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (p) {
          Quick.product = p;
          Quick.displayName = name || p.title;
          Quick.options = {};
          var first = p.variants.filter(function (v) { return v.available; })[0] || p.variants[0];
          (p.options || []).forEach(function (o, i) {
            var oname = typeof o === 'string' ? o : o.name;
            Quick.options[oname] = first['option' + (i + 1)];
          });
          Quick.paint();
        })
        .catch(function () { window.location.href = url; });
    },

    close: function () {
      if (!Quick.el) return;
      Quick.el.hidden = true;
      var drawerOpen = Drawer.el && Drawer.el.classList.contains('is-open');
      var themeOpen = Drawer.themeEl && Drawer.themeEl.classList.contains('active');
      if (!drawerOpen && !themeOpen) root.style.overflow = '';
    },

    optionNames: function () {
      return (Quick.product.options || []).map(function (o) {
        return typeof o === 'string' ? o : o.name;
      });
    },

    match: function () {
      var names = Quick.optionNames();
      return Quick.product.variants.filter(function (v) {
        return names.every(function (n, i) { return v['option' + (i + 1)] === Quick.options[n]; });
      })[0];
    },

    choose: function (name, value) {
      Quick.options[name] = value;
      Quick.paint();
    },

    money: function (cents) {
      return '£' + (cents / 100).toFixed(2);
    },

    paint: function () {
      var p = Quick.product;
      var v = Quick.match();
      var names = Quick.optionNames();

      var groups = names.map(function (n, i) {
        var seen = [];
        p.variants.forEach(function (variant) {
          var val = variant['option' + (i + 1)];
          if (seen.indexOf(val) === -1) seen.push(val);
        });
        var buttons = seen.map(function (val) {
          var possible = p.variants.some(function (variant) {
            return variant['option' + (i + 1)] === val && variant.available;
          });
          return '<button type="button" class="lmh-opt' +
            (Quick.options[n] === val ? ' is-on' : '') +
            (possible ? '' : ' is-out') +
            '" data-lmh-opt data-lmh-opt-name="' + n + '" data-lmh-opt-value="' +
            String(val).replace(/"/g, '&quot;') + '">' + val + '</button>';
        }).join('');
        return '<div class="lmh-quick__opt"><span class="lmh-quick__optname">' + n + '</span><div class="lmh-opts">' + buttons + '</div></div>';
      }).join('');

      var img = v && v.featured_image ? v.featured_image.src : p.featured_image;
      var available = v && v.available;

      $('[data-lmh-quick-body]', Quick.el).innerHTML =
        '<div class="lmh-quick__head">' +
          '<h2 class="lmh-h3">' + Quick.displayName + '</h2>' +
          '<button class="lmh-drawer__close" type="button" data-lmh-quick-close aria-label="Close">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="lmh-quick__main">' +
          (img ? '<img class="lmh-quick__img" src="' + img + '" alt="" loading="lazy">' : '') +
          '<div class="lmh-quick__side">' +
            '<p class="lmh-quick__price">' + (v ? Quick.money(v.price) : '') +
              (v && v.compare_at_price > v.price ? ' <s>' + Quick.money(v.compare_at_price) + '</s>' : '') +
            '</p>' +
            groups +
            '<form method="post" action="/cart/add" data-lmh-add>' +
              '<input type="hidden" name="id" value="' + (v ? v.id : '') + '">' +
              '<input type="hidden" name="quantity" value="1">' +
              '<button type="submit" class="lmh-btn lmh-btn--block"' + (available ? '' : ' disabled') + '>' +
                '<span class="lmh-btn__label">' + (available ? 'Add to cart' : 'Sold out') + '</span>' +
                '<span class="lmh-btn__done">Added</span>' +
              '</button>' +
            '</form>' +
            '<a class="lmh-textlink lmh-quick__full" href="' + p.url + '">See the full product page</a>' +
          '</div>' +
        '</div>';
    }
  };

  function bindQuickView() {
    once('quick', function () {
      on(document, 'click', function (e) {
        var trigger = e.target.closest('[data-lmh-quickview]');
        if (!trigger) return;
        e.preventDefault();
        Quick.open(trigger.dataset.lmhQuickview, trigger.dataset.lmhQuickviewName);
      });
    });
  }

  /* --------------------------------------------------------- room swap ---
     The advert link can carry ?room=bedroom. The hero image is handled inline
     in the section so the right one loads first time; this swaps the words and
     floats that room's products to the front of the grid. */
  function bindRoomSwap() {
    var room;
    try {
      room = (new URLSearchParams(window.location.search).get('room') || '').toLowerCase().trim();
    } catch (e) { return; }
    if (!room) return;

    var node = $('[data-lmh-rooms]');
    if (node) {
      var map = {};
      try { map = JSON.parse(node.textContent); } catch (e) {}
      var hit = map[room];
      if (hit) {
        var eyebrow = $('[data-lmh-room-eyebrow]');
        var heading = $('[data-lmh-room-heading]');
        var sub = $('[data-lmh-room-sub]');
        if (eyebrow && hit.eyebrow) eyebrow.textContent = hit.eyebrow;
        if (sub && hit.sub) sub.textContent = hit.sub;
        if (heading && hit.heading) {
          var text = hit.heading;
          if (hit.glow && text.indexOf(hit.glow) !== -1) {
            var span = document.createElement('span');
            span.className = 'lmh-glow';
            span.textContent = hit.glow;
            heading.textContent = '';
            var parts = text.split(hit.glow);
            heading.appendChild(document.createTextNode(parts[0]));
            heading.appendChild(span);
            heading.appendChild(document.createTextNode(parts.slice(1).join(hit.glow)));
          } else {
            heading.textContent = text;
          }
        }
      }
    }

    // Float this room's products to the front without hiding anything else.
    $$('[data-lmh-card-room]').forEach(function (card) {
      if (card.dataset.lmhCardRoom === room) card.style.order = '-1';
    });
  }

  /* ------------------------------------------------------- anchor scrolling */
  function bindAnchors() {
    $$('[data-lmh-anchor]').forEach(function (link) {
      if (!markOnce(link)) return;
      on(link, 'click', function (e) {
        var id = link.getAttribute('href');
        if (!id || id.charAt(0) !== '#') return;
        var target = document.querySelector(id);
        if (!target) return;
        e.preventDefault();
        var header = $('[data-lmh-header]');
        var offset = header ? header.offsetHeight : 0;
        var y = target.getBoundingClientRect().top + window.pageYOffset - offset - 8;
        window.scrollTo({ top: y, behavior: reduced ? 'auto' : 'smooth' });
        target.setAttribute('tabindex', '-1');
        window.setTimeout(function () { target.focus({ preventScroll: true }); }, reduced ? 0 : 500);
      });
    });
  }

  /* ------------------------------------------------------- header + sticky */
  function bindScrollUI() {
    var header = $('[data-lmh-header]');
    var sticky = $('[data-lmh-sticky]');
    var trigger = $('[data-lmh-sticky-after]');
    if (!header && !sticky) return;

    var ticking = false;
    function update() {
      var y = window.pageYOffset;
      if (header && header.dataset.lmhTransparent === 'true') {
        var on = header.classList.contains('lmh-header--stuck');
        if (!on && y > 90) header.classList.add('lmh-header--stuck');
        else if (on && y < 30) header.classList.remove('lmh-header--stuck');
      }
      if (sticky) {
        var vis = sticky.classList.contains('is-visible');
        var edge = trigger ? trigger.getBoundingClientRect().bottom : 600 - y;
        if (!vis && edge < -40) sticky.classList.add('is-visible');
        else if (vis && edge > 20) sticky.classList.remove('is-visible');
      }
      ticking = false;
    }
    if (!markOnce(header || sticky)) return;
    on(window, 'scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  }

  /* -------------------------------------------------------------- search */
  function bindSearch() {
    var panel = $('[data-lmh-search]');
    if (!panel || !markOnce(panel)) return;
    var toggles = $$('[data-lmh-search-toggle]');

    var header = $('[data-lmh-header]');

    function setOpen(open) {
      panel.hidden = !open;
      if (header) header.classList.toggle('lmh-header--searching', open);
      toggles.forEach(function (t) {
        if (t.hasAttribute('aria-expanded')) t.setAttribute('aria-expanded', String(open));
      });
      if (open) {
        var input = $('[data-lmh-search-input]', panel);
        if (input) input.focus();
      }
    }

    toggles.forEach(function (t) {
      on(t, 'click', function () { setOpen(panel.hidden); });
    });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) setOpen(false);
    });
  }

  /* ------------------------------ measure the chrome above the hero -------
     The hero is sized to the viewport on small screens. The announcement bar
     sits above it, so the hero has to know how tall that is or the button
     drops below the fold. */
  function measureChrome() {
    var announce = $('[data-lmh-announce]');
    var h = announce ? Math.round(announce.getBoundingClientRect().height) : 0;
    root.style.setProperty('--lmh-chrome-h', h + 'px');
  }

  /* -------------------------------------------------- reviews fallback ---
     Judge.me renders in the browser. If its carousel has not filled in shortly
     after load, show our own hand-picked marquee rather than a blank section. */
  function bindReviewsFallback() {
    var host = $('[data-lmh-jdgm]');
    var fallback = $('[data-lmh-fallback]');
    if (!host || !fallback || !markOnce(fallback)) return;

    var tries = 0;
    var timer = window.setInterval(function () {
      tries++;
      var widget = host.querySelector('.jdgm-carousel');
      var filled = widget && widget.children.length > 0 && widget.offsetHeight > 40;

      if (filled) {
        window.clearInterval(timer);
        return;
      }
      if (tries > 12) { // roughly six seconds
        window.clearInterval(timer);
        host.hidden = true;
        fallback.hidden = false;
      }
    }, 500);
  }

  /* ------------------------------------------------- announcement rotator */
  function bindRotator() {
    var box = $('[data-lmh-rotator]');
    if (!box || !markOnce(box)) return;
    var items = $$('span', box);
    if (items.length < 2) return;
    if (reduced) return; // leave the first message showing

    var i = 0;
    var every = (parseInt(box.dataset.lmhRotateEvery, 10) || 5) * 1000;
    window.setInterval(function () {
      items[i].classList.remove('is-on');
      i = (i + 1) % items.length;
      items[i].classList.add('is-on');
    }, every);
  }

  /* ------------------------------------------------------------- reveals */
  function bindReveals() {
    if (!('IntersectionObserver' in window)) {
      $$('.lmh-reveal').forEach(function (el) { el.classList.add('is-in'); });
      $$('[data-lmh-layer]').forEach(function (el) { el.classList.add('is-on'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    $$('.lmh-reveal').forEach(function (el) { io.observe(el); });

    // The three layer images switch on in order, like turning on lamps.
    var layerIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var delay = reduced ? 0 : parseInt(entry.target.dataset.lmhLayer, 10) * 340;
        window.setTimeout(function () { entry.target.classList.add('is-on'); }, delay);
        layerIO.unobserve(entry.target);
      });
    }, { threshold: 0.35 });
    $$('[data-lmh-layer]').forEach(function (el) { layerIO.observe(el); });
  }

  /* ------------------------------------------------------------ accordion */
  function bindAccordion() {
    $$('[data-lmh-faq]').forEach(function (list) {
      if (list.dataset.lmhFaq !== 'single' || !markOnce(list)) return;
      var items = $$('details', list);
      items.forEach(function (item) {
        on(item, 'toggle', function () {
          if (!item.open) return;
          items.forEach(function (other) {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  /* ---------------------------------------------------------------- boot */
  function init() {
    Drawer.init();
    bindAddToCart();
    bindAnchors();
    bindScrollUI();
    bindReveals();
    bindAccordion();
    bindSearch();
    bindQuickView();
    bindReviewsFallback();
    bindRotator();
    bindRoomSwap();
    measureChrome();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind when the theme editor re-renders a section
  document.addEventListener('shopify:section:load', init);

  window.addEventListener('resize', measureChrome, { passive: true });

  window.LMHDrawer = Drawer;
})();
